import { type Interaction, type Plugin } from '@interactjs/types';

type HoldTimer = Parameters<typeof clearTimeout>[0];

interface IInteractionSignal {
  interaction: Interaction;
}

function isHoldTimer(value: unknown): value is HoldTimer {
  return typeof value === 'number' || (typeof value === 'object' && !!value);
}

function clearHoldTimer({ interaction }: IInteractionSignal): void {
  const holdTimer: unknown = interaction.autoStartHoldTimer;
  if (isHoldTimer(holdTimer)) {
    clearTimeout(holdTimer);
  }
  interaction.autoStartHoldTimer = undefined;
}

/**
 * Works around a timer leak in the interactjs (1.10.x) hold plugin. It schedules
 * a new hold timer on every `autoStart:prepared` without clearing the previous
 * one, and a hovering mouse re-prepares on every move. A leaked timer that fires
 * while the button is down, after a drag cancelled the hold (`prepared.name` is
 * null), calls `Interaction.start` and throws "Cannot read properties of
 * undefined (reading 'enabled')", which breaks dragging on the timeline.
 *
 * Runs before the hold plugin so a pending timer is cleared before the next one
 * is scheduled, and again whenever the hold is cancelled or the gesture ends.
 */
export const INTERACT_HOLD_TIMER_PLUGIN: Plugin = {
  id: 'principle/hold-timer-cleanup',
  before: ['auto-start/hold'],
  listeners: {
    'autoStart:prepared': clearHoldTimer,
    'autoStart:before-start': clearHoldTimer,
    'interactions:up': clearHoldTimer,
    'interactions:stop': clearHoldTimer,
  },
};
